import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Trash2, Download, Trash, Image, Upload, Package, Calendar, Truck, User, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import Sidebar from '../components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { motion, AnimatePresence } from 'framer-motion'; // Import for animations
import Topbar from '../components/Topbar';
import imageCompression from 'browser-image-compression';
// Import API functions
import { 
  getShipmentByReference, 
  uploadEvidence, 
  uploadPhotos, 
  updatePodTime,
  deleteEvidence,
  deletePhoto,
  deleteAllPhotos,
  getInboundShipmentByReference,
  uploadInboundEvidence,
  uploadInboundPhotos,
  deleteInboundEvidence,
  deleteInboundPhoto,
  deleteAllInboundPhotos
} from '../api';

// Task type selection component
const TaskTypeSelection = ({ onSelect }) => {
  return (
    <div className="flex flex-col items-center p-8">
      <h2 className="text-2xl font-bold mb-8 text-gray-800">Selecciona el tipo de evidencia</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-2xl">
        <button 
          onClick={() => onSelect('inbound')}
          className="flex flex-col items-center justify-center p-8 bg-sky-100 hover:bg-sky-200 border-2 border-sky-300 rounded-xl transition-all transform hover:scale-105"
        >
          <div className="w-24 h-24 rounded-full bg-sky-600 flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
            </svg>
          </div>
          <h3 className="text-xl font-bold text-sky-700">Inbound</h3>
          <p className="text-sm text-gray-600 text-center mt-2">Gestionar evidencia de recepción de mercancía</p>
        </button>
        
        <button 
          onClick={() => onSelect('outbound')}
          className="flex flex-col items-center justify-center p-8 bg-purple-100 hover:bg-purple-200 border-2 border-purple-300 rounded-xl transition-all transform hover:scale-105"
        >
          <div className="w-24 h-24 rounded-full bg-purple-600 flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
          </div>
          <h3 className="text-xl font-bold text-purple-700">Outbound</h3>
          <p className="text-sm text-gray-600 text-center mt-2">Gestionar evidencia de entrega de mercancía</p>
        </button>
      </div>
    </div>
  );
};

// New component for displaying shipment details
const ShipmentDetailsCard = ({ shipment, taskType }) => {
  if (!shipment) return null;
  
  const isInbound = taskType === 'inbound';
  
  // Format the details based on shipment type
  const details = isInbound ? {
    reference: shipment.referencia,
    date: new Date(shipment.fecha_de_carga).toLocaleDateString(),
    driver: `${shipment.operador_nombre || ''} ${shipment.operador_apellido_paterno || ''}`,
    vehicle: shipment.vehiculo_placas,
    status: shipment.estatus,
    company: shipment.nombre_cuenta || 'No especificada',
    email: shipment.operador_email,
    phone: shipment.operador_telefono
  } : {
    reference: shipment.reference,
    date: shipment.pickup_date ? new Date(shipment.pickup_date).toLocaleDateString() : 'No asignada',
    driver: shipment.driver || 'No asignado',
    vehicle: shipment.vehicle || 'No asignado',
    status: shipment.status,
    destination: shipment.destination || 'No especificado',
    customer: shipment.customer || 'No especificado'
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 shadow-md rounded-lg mb-6"
    >
      <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
        <div className="flex items-center">
          <Package className="mr-2 text-gray-700" size={28} />
          <div>
            <h3 className="text-xl font-bold text-gray-800">{details.reference}</h3>
            <p className="text-sm text-gray-500">
              {isInbound ? 'Embarque de entrada' : 'Embarque de salida'}
            </p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full font-medium ${
          details.status?.includes('COMPLETADO') || details.status?.includes('ENTREGADO') ? 
          'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {details.status}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="flex items-start">
          <Calendar className="mr-2 text-gray-600 mt-1" size={18} />
          <div>
            <p className="text-xs text-gray-500 font-medium">Fecha</p>
            <p className="text-sm">{details.date}</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <User className="mr-2 text-gray-600 mt-1" size={18} />
          <div>
            <p className="text-xs text-gray-500 font-medium">Operador</p>
            <p className="text-sm">{details.driver}</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <Truck className="mr-2 text-gray-600 mt-1" size={18} />
          <div>
            <p className="text-xs text-gray-500 font-medium">Vehículo</p>
            <p className="text-sm">{details.vehicle}</p>
          </div>
        </div>
        
        {isInbound ? (
          <>
            <div className="flex items-start">
              <Mail className="mr-2 text-gray-600 mt-1" size={18} />
              <div>
                <p className="text-xs text-gray-500 font-medium">Correo</p>
                <p className="text-sm">{details.email}</p>
              </div>
            </div>
            <div className="flex items-start">
              <Phone className="mr-2 text-gray-600 mt-1" size={18} />
              <div>
                <p className="text-xs text-gray-500 font-medium">Teléfono</p>
                <p className="text-sm">{details.phone}</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="mr-2 text-gray-600 mt-1">🏢</div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Cuenta</p>
                <p className="text-sm">{details.company}</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start">
              <div className="mr-2 text-gray-600 mt-1">📍</div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Destino</p>
                <p className="text-sm">{details.destination}</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="mr-2 text-gray-600 mt-1">👤</div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Cliente</p>
                <p className="text-sm">{details.customer}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Empty state component for evidence sections
const EmptyEvidenceState = ({ type, onClick }) => {
  const isPhotos = type === 'photos';
  
  return (
    <motion.div 
      whileHover={{ scale: 1.02 }}
      className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-10 px-6 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      onClick={onClick}
    >
      {isPhotos ? (
        <Image size={64} className="text-gray-400 mb-4" />
      ) : (
        <FileText size={64} className="text-gray-400 mb-4" />
      )}
      <h3 className="text-xl font-bold text-gray-700 mb-2">
        Sin {isPhotos ? 'Fotos' : 'Evidencia'}
      </h3>
      <p className="text-gray-500 text-center mb-4">
        No hay {isPhotos ? 'fotografías' : 'documentos'} cargados para este embarque
      </p>
      <div className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center">
        <Upload className="mr-2" size={18} />
        Toque para cargar
      </div>
    </motion.div>
  );
};

// FileUploadSection component
const FileUploadSection = ({ title, getRootProps, getInputProps, files, isImage = false, onRemove }) => {
  return (
    <div className="w-full">
      <h4 className="text-base font-semibold mb-4">{title}</h4>
      
      <div {...getRootProps()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 flex flex-col items-center justify-center hover:border-sky-500 cursor-pointer transition-colors">
        <input {...getInputProps()} />
        {files && files.length > 0 ? (
          <div className="text-center">
            <p className="font-medium text-sky-700">{files.length} archivo(s) seleccionado(s)</p>
            <p className="text-sm text-gray-500">Haga clic o arrastre para agregar más</p>
          </div>
        ) : (
          <>
            {isImage ? (
              <Image size={48} className="text-gray-400 mb-3" />
            ) : (
              <FileText size={48} className="text-gray-400 mb-3" />
            )}
            <p className="text-center text-sm text-gray-500 mb-2">
              Arrastra y suelta {isImage ? "imágenes" : "documentos"} aquí o haz clic para seleccionar
            </p>
            <p className="text-xs text-gray-400">
              {isImage ? "JPG, PNG, etc." : "PDF, Word, etc."}
            </p>
          </>
        )}
      </div>
      
      {files && files.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50">
              <div className="flex items-center">
                {isImage ? (
                  <div className="w-10 h-10 rounded bg-gray-200 mr-3 flex items-center justify-center overflow-hidden">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={`Preview ${index}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <FileText className="text-sky-700 mr-3" size={24} />
                )}
                <div>
                  <p className="font-medium text-sm truncate max-w-xs">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
                className="bg-red-100 text-red-700 hover:bg-red-200 p-1 h-auto rounded-full"
                size="sm"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Evidence = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('evidence');
  const [shipmentReference, setShipmentReference] = useState('');
  const [shipmentSearched, setShipmentSearched] = useState(false);
  const [shipmentData, setShipmentData] = useState(null);
  const [shipmentId, setShipmentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [showTaskSelection, setShowTaskSelection] = useState(true);
  
  // Aseguramos que siempre se muestre la selección de tipo de tarea al cargar la página
  useEffect(() => {
    // Resetear el estado de selección de tarea cuando se monta el componente
    setTaskType(null);
    setShowTaskSelection(true);
  }, []);

  // Evidencia PDF
  const [hasEvidence, setHasEvidence] = useState(false);
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [evidenceId, setEvidenceId] = useState('');
  const [file1, setFile1] = useState(null);
  const [file1Name, setFile1Name] = useState('');
  const [file1Date, setFile1Date] = useState('');
  const [showEvidenceUpload, setShowEvidenceUpload] = useState(false);
  
  // Evidencia fotográfica
  const [hasPhotos, setHasPhotos] = useState(false);
  const [photosIds, setPhotosIds] = useState([]);
  const [photosUrls, setPhotosUrls] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [showPhotosUpload, setShowPhotosUpload] = useState(false);

  // Función para comprimir imágenes
  const compressImage = async (imageFile) => {
    const options = {
      maxSizeMB: 1, // máximo 1MB
      maxWidthOrHeight: 1920, // máxima dimensión 1920px
      useWebWorker: true,
      initialQuality: 0.7, // calidad inicial 70%
    };
    
    try {
      toast.info(`Comprimiendo imagen: ${imageFile.name}`, { autoClose: 2000 });
      const compressedFile = await imageCompression(imageFile, options);
      console.log(`Tamaño original: ${imageFile.size / 1024 / 1024} MB`);
      console.log(`Tamaño comprimido: ${compressedFile.size / 1024 / 1024} MB`);
      return compressedFile;
    } catch (error) {
      console.error('Error al comprimir imagen:', error);
      toast.error(`No se pudo comprimir ${imageFile.name}`, { autoClose: 3000 });
      return imageFile; // Si falla la compresión, retorna el archivo original
    }
  };

  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const handleSearchShipment = async () => {
    if (!shipmentReference.trim()) {
      toast.warning('Por favor ingrese una referencia de embarque');
      return;
    }
    
    // Verificar que se haya seleccionado un tipo de tarea
    if (taskType === null) {
      toast.warning('Por favor seleccione un tipo de tarea (Inbound/Outbound) primero');
      return;
    }
    
    setIsLoading(true);
    setShowEvidenceUpload(false);
    setShowPhotosUpload(false);
    
    try {
      const response = taskType === 'inbound' 
        ? await getInboundShipmentByReference(shipmentReference)
        : await getShipmentByReference(shipmentReference);
      
      setShipmentSearched(true);
      
      if (response) {
        setShipmentData(response);
        setShipmentId(response.id);
        
        // Verificar evidencia PDF
        if ((taskType === 'inbound' ? response.evidence_drive_id : response.evidence_drive_id)) {
          setHasEvidence(true);
          const publicUrl = `https://drive.google.com/file/d/${response.evidence_drive_id}/preview`;
          setEvidenceUrl(publicUrl);
          setEvidenceId(response.evidence_drive_id);
        } else {
          setHasEvidence(false);
          setEvidenceUrl('');
        }

        // Verificar evidencia fotográfica
        if (response.photos_drive_ids && response.photos_drive_ids.length > 0) {
          setHasPhotos(true);
          setPhotosUrls(response.photos_drive_ids.map(id => 
            `https://drive.google.com/file/d/${id}/preview`
          ));
          setPhotosIds(response.photos_drive_ids);
        } else {
          setHasPhotos(false);
          setPhotosUrls([]);
          setPhotosIds([]);
        }
        
        toast.success(`Embarque ${shipmentReference} encontrado`);
      } else {
        setShipmentData(null);
        setHasEvidence(false);
        setEvidenceUrl('');
        setHasPhotos(false);
        setPhotosUrls([]);
        setPhotosIds([]);
        toast.error('Embarque no encontrado.');
      }
    } catch (error) {
      console.error('Error al buscar el embarque:', error);
      setShipmentData(null);
      setHasEvidence(false);
      setEvidenceUrl('');
      setHasPhotos(false);
      setPhotosUrls([]);
      setPhotosIds([]);
      toast.error('Error al buscar el embarque');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (acceptedFiles) => {
    const currentDateTime = new Date().toLocaleString();
    const file = acceptedFiles[0];
    setFile1(file);
    setFile1Date(currentDateTime);
    setFile1Name(file.name);
  };

  const handleFileRemove = () => {
    setFile1(null);
    setFile1Date('');
    setFile1Name('');
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    const maxRetries = 3;
    let currentTry = 0;
  
    try {
      const uploadFunction = taskType === 'inbound' ? uploadInboundEvidence : uploadEvidence;
        if (file1) {
        currentTry = 0;
        while (currentTry < maxRetries) {
          try {
            await uploadFunction(shipmentId, file1);
            toast.success('Documento cargado correctamente');
            break;
          } catch (error) {
            currentTry++;
            if (currentTry === maxRetries) {
              throw error;
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
            toast.info(`Reintentando subida... Intento ${currentTry} de ${maxRetries}`);
          }
        }
      }
      
      // Update POD time for all shipment types (both outbound and inbound)
      const currentDateTime = new Date().toISOString();
      try {
        await updatePodTime(shipmentReference, currentDateTime);
        toast.success('Hora del POD actualizada correctamente');
      } catch (error) {
        console.error('Error actualizando POD time:', error);
        toast.warning('No se pudo actualizar la hora del POD');
      }
      
      setShowEvidenceUpload(false);
      // Buscar el embarque nuevamente para actualizar la vista
      await handleSearchShipment();
  
    } catch (error) {
      console.error('Error al subir el documento:', error);
      toast.error('Error al subir el documento. Por favor, inténtelo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvidence = async () => {
    toast.info(
      <div>
        <p>¿Estás seguro de que deseas borrar la evidencia?</p>
        <div className="flex gap-2 mt-2">
          <button
            className="p-1 bg-red-500 text-white rounded-md"
            onClick={async () => {
              try {
                if (taskType === 'inbound') {
                  await deleteInboundEvidence(shipmentId);
                } else {
                  await deleteEvidence(shipmentId, evidenceId);
                }
                
                setHasEvidence(false);
                setEvidenceUrl('');
                setEvidenceId('');
                toast.success('Evidencia eliminada correctamente');
              } catch (error) {
                console.error('Error al eliminar la evidencia:', error);
                toast.error('Error al eliminar la evidencia');
              }
              toast.dismiss();
            }}
          >
            Sí, borrar
          </button>
          <button
            className="p-1 bg-gray-500 text-white rounded-md"
            onClick={() => toast.dismiss()}
          >
            Cancelar
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
      }
    );
  };  const handleUploadPhotos = async () => {
    if (photoFiles.length === 0) return;
    
    setIsLoading(true);
    try {
      const uploadFunction = taskType === 'inbound' ? uploadInboundPhotos : uploadPhotos;
      const compressedFiles = await Promise.all(photoFiles.map(file => compressImage(file)));
      const response = await uploadFunction(shipmentId, compressedFiles);
      
      // Actualizar los estados con las nuevas fotos
      if (response.file_ids) {
        setPhotosIds(prev => [...prev, ...response.file_ids]);
        setPhotosUrls(prev => [
          ...prev,
          ...response.file_ids.map(id => `https://drive.google.com/file/d/${id}/preview`)
        ]);
        setHasPhotos(true);
      }
      
      // No actualizamos POD time al cargar fotos, solo photo_operador
      // (Esto se maneja automáticamente en el backend)
      
      setShowPhotosUpload(false);
      toast.success('Fotos cargadas correctamente');
      // Refresh data
      await handleSearchShipment();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar las fotos');
    } finally {
      setIsLoading(false);
      setPhotoFiles([]); // Limpiar los archivos seleccionados después de la carga
    }
  };

  const handleDeletePhotos = async () => {
    toast.info(
      <div>
        <p>¿Estás seguro de que deseas borrar las fotos?</p>
        <div className="flex gap-2 mt-2">
          <button
            className="p-1 bg-red-500 text-white rounded-md"
            onClick={async () => {
              try {
                if (taskType === 'inbound') {
                  await deleteAllInboundPhotos(shipmentId);
                } else {
                  await deleteAllPhotos(shipmentId);
                }
                
                setHasPhotos(false);
                setPhotosUrls([]);
                setPhotosIds([]);
                toast.success('Fotos eliminadas correctamente');
              } catch (error) {
                console.error('Error al eliminar las fotos:', error);
                toast.error('Error al eliminar las fotos');
              }
              toast.dismiss();
            }}
          >
            Sí, borrar
          </button>
          <button
            className="p-1 bg-gray-500 text-white rounded-md"
            onClick={() => toast.dismiss()}
          >
            Cancelar
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
      }
    );
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      if (taskType === 'inbound') {
        await deleteInboundPhoto(shipmentId, photoId);
      } else {
        await deletePhoto(shipmentId, photoId);
      }
      
      setPhotosIds(prev => prev.filter(id => id !== photoId));
      setPhotosUrls(prev => prev.filter((_, idx) => photosIds[idx] !== photoId));
      toast.success('Foto eliminada correctamente');
      
      if (photosIds.length === 1) {
        setHasPhotos(false);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Filtrar para asegurar que solo se acepten archivos PDF
      const pdfFiles = acceptedFiles.filter(file => 
        file.type === 'application/pdf'
      );
      
      if (pdfFiles.length < acceptedFiles.length) {
        toast.error('Solo se permiten archivos PDF');
      }
      
      if (rejectedFiles && rejectedFiles.length > 0) {
        toast.error('Algunos archivos no son PDF válidos');
      }
      
      if (pdfFiles.length > 0) {
        handleFileSelect(pdfFiles);
      }
    },
    multiple: false,
    accept: {
      'application/pdf': ['.pdf']
    },
    validator: (file) => {
      if (file.type !== 'application/pdf') {
        return {
          code: "file-invalid-type",
          message: "Solo se permiten archivos PDF"
        };
      }
      return null;
    }
  });

  const { getRootProps: getRootPropsPhotos, getInputProps: getInputPropsPhotos } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Filtrar para asegurar que solo se acepten imágenes
      const imageFiles = acceptedFiles.filter(file => 
        file.type.startsWith('image/')
      );
      
      if (imageFiles.length < acceptedFiles.length) {
        toast.error('Solo se permiten archivos de imagen');
      }
      
      if (rejectedFiles && rejectedFiles.length > 0) {
        toast.error('Algunos archivos no son imágenes válidas');
      }
      
      setPhotoFiles(prev => [...prev, ...imageFiles]);
    },
    multiple: true,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp', '.tiff']
    },
    validator: (file) => {
      if (!file.type.startsWith('image/')) {
        return {
          code: "file-invalid-type",
          message: "Solo se permiten archivos de imagen"
        };
      }
      return null;
    }
  });

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />      <div className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        {showTaskSelection ? (
          // Task type selection screen
          <div className="bg-white p-6 shadow-md rounded-lg mt-6">
            <TaskTypeSelection onSelect={(type) => {
              setTaskType(type);
              setShowTaskSelection(false);
            }} />
          </div>
        ) : (
          // Existing functionality after task type selection
          <>
            <Topbar 
              searchValue={shipmentReference}
              onSearchChange={(value) => setShipmentReference(value)}
            />            <div className="flex items-center mt-6 mb-4">
              <button 
                onClick={() => {
                  setShowTaskSelection(true);
                  setTaskType(null);
                  setShipmentData(null);
                  setShipmentSearched(false);
                  setHasEvidence(false);
                  setHasPhotos(false);
                }} 
                className="flex items-center text-sky-600 hover:text-sky-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                <span className="ml-2">Volver a selección</span>
              </button>
              <div className="ml-4 px-4 py-1 rounded-full bg-gradient-to-r from-sky-500 to-purple-500 text-white font-medium">
                {taskType === 'inbound' ? 'Evidencia de Recepción' : 'Evidencia de Entrega'}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-1 gap-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 shadow-md rounded-lg"
              >
                <h2 className="text-lg font-bold mb-4">Buscar Embarque</h2>
                <div className="flex gap-2">
                  <input 
                    type="text"
                    placeholder="Referencia del Embarque"
                    value={shipmentReference}
                    onChange={(e) => setShipmentReference(e.target.value)}
                    className="flex-1 p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <Button onClick={handleSearchShipment} className="bg-sky-600 text-white hover:bg-sky-700 rounded-lg">
                    Buscar
                  </Button>
                </div>
              </motion.div>

              {shipmentSearched && shipmentData && (
                <ShipmentDetailsCard 
                  shipment={shipmentData} 
                  taskType={taskType}
                />
              )}

              {shipmentSearched && shipmentData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Photo Evidence Section */}
                  <AnimatePresence mode="wait">
                    <motion.div 
                      key={`photos-${hasPhotos}-${showPhotosUpload}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white p-6 shadow-md rounded-lg h-full flex flex-col"
                    >
                      <h3 className="text-lg font-semibold mb-6">Evidencia Fotográfica</h3>
                      {hasPhotos && !showPhotosUpload ? (
                        <div className="flex-1 flex flex-col">
                          <div className="border border-gray-300 p-4 rounded-lg flex-1">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                              {photosUrls.map((url, index) => (
                                <div key={photosIds[index]} className="relative">
                                  <iframe 
                                    title={`Foto ${index + 1}`}
                                    src={url}
                                    className="w-full aspect-square rounded-lg"
                                    style={{ border: 'none' }}
                                  />
                                  <Button
                                    onClick={() => handleDeletePhoto(photosIds[index])}
                                    className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full"
                                    size="sm"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="mt-4 flex justify-between items-center">
                            <Button
                              onClick={() => setShowPhotosUpload(true)}
                              className="bg-sky-700 text-white rounded-lg hover:bg-sky-800 flex items-center"
                            >
                              <Upload className="mr-2" size={18} />
                              Agregar más fotos
                            </Button>
                            <Button
                              onClick={handleDeletePhotos}
                              className="bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                            >
                              <Trash size={18} /> Borrar todo
                            </Button>
                          </div>
                        </div>
                      ) : showPhotosUpload ? (
                        <div className="flex-1 flex flex-col">
                          <div className="flex-1">
                            <FileUploadSection
                              title="Carga de evidencia fotográfica"
                              getRootProps={getRootPropsPhotos}
                              getInputProps={getInputPropsPhotos}
                              files={photoFiles}
                              isImage={true}
                              onRemove={(index) => {
                                const newFiles = [...photoFiles];
                                newFiles.splice(index, 1);
                                setPhotoFiles(newFiles);
                              }}
                            />
                          </div>
                          <div className="mt-6 flex justify-between gap-4">
                            <Button
                              onClick={() => setShowPhotosUpload(false)}
                              className="bg-gray-500 text-white hover:bg-gray-600 rounded-lg"
                            >
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleUploadPhotos}
                              disabled={photoFiles.length === 0 || isLoading}
                              className="bg-sky-700 text-white hover:bg-sky-800 rounded-lg"
                            >
                              {isLoading ? 'Cargando...' : 'Guardar Fotos'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <EmptyEvidenceState 
                          type="photos" 
                          onClick={() => setShowPhotosUpload(true)} 
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Document Evidence Section */}
                  <AnimatePresence mode="wait">
                    <motion.div 
                      key={`evidence-${hasEvidence}-${showEvidenceUpload}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white p-6 shadow-md rounded-lg h-full flex flex-col"
                    >
                      <h3 className="text-lg font-semibold mb-6">Evidencia Documental</h3>
                      {hasEvidence && !showEvidenceUpload ? (
                        <div className="flex-1 flex flex-col">
                          <div className="border border-gray-300 p-4 rounded-lg flex-1">
                            <iframe 
                              title="Evidencia PDF"
                              src={evidenceUrl}
                              className="w-full h-full rounded-lg"
                              style={{ border: 'none', minHeight: '400px' }}
                            />
                          </div>
                          <div className="mt-4 flex justify-between">
                            <Button
                              onClick={() => setShowEvidenceUpload(true)}
                              className="bg-sky-700 text-white hover:bg-sky-800 rounded-lg"
                            >
                              <Upload className="mr-2" size={18} /> Actualizar documento
                            </Button>
                            <Button
                              onClick={handleDeleteEvidence}
                              className="bg-red-500 text-white hover:bg-red-600 rounded-lg"
                            >
                              <Trash size={18} className="mr-2" /> Borrar documento
                            </Button>
                          </div>
                        </div>
                      ) : showEvidenceUpload ? (
                        <div className="flex-1 flex flex-col">
                          <div className="flex-1">
                            <FileUploadSection
                              title="Carga de evidencia documental"
                              getRootProps={getRootProps1}
                              getInputProps={getInputProps1}
                              files={file1 ? [file1] : []}
                              isImage={false}
                              onRemove={() => handleFileRemove()}
                            />
                          </div>

                          <div className="mt-6 flex justify-between">
                            <Button
                              onClick={() => setShowEvidenceUpload(false)}
                              className="bg-gray-500 text-white hover:bg-gray-600 rounded-lg"
                            >
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleSaveChanges}
                              disabled={!file1 || isLoading}
                              className="bg-sky-700 text-white hover:bg-sky-800 rounded-lg"
                            >
                              {isLoading ? (
                                <>
                                  <span className="mr-2">Cargando...</span>
                                  <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                                </>
                              ) : (
                                'Guardar Cambios'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <EmptyEvidenceState 
                          type="document" 
                          onClick={() => setShowEvidenceUpload(true)} 
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Evidence;